// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 17.9.0 — Tour engine hook managing driver.js lifecycle + action execution.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Note: `TourScriptStep` is the tour-script.ts TourStep (re-exported with an
// alias because `onboarding.ts` owns a separate, unrelated TourStep shape).
import type { TourScript, TourScriptStep, TourAction } from '@vn-grc/shared';
import 'driver.js/dist/driver.css';
import { driver as createDriver } from 'driver.js';
import { useLocale } from './useLocale.js';
import {
  executeTourAction,
  findVisibleTourTarget,
  createDriverAriaGuard,
  waitForTourElement,
  type DriverAriaGuard,
} from './tour-engine-dom.js';
import { logWarning } from '../utils/optional-operation-log.js';

/**
 * 44.1.0 — VI fallback helpers for TourScript / TourStep. When the active locale
 * resolves to Vietnamese and the script/step provides `title_vi` / `description_vi`,
 * the runner prefers them; otherwise it falls back to the EN field. All 28 tours
 * gain VI parallels in Sprint 44.1.8; scripts without VI keep working unchanged.
 */
function isViLocale(locale: string): boolean {
  const prefix = (locale || '').split('-')[0].toLowerCase();
  return prefix === 'vi';
}

export function resolveTourStepTitle(step: TourScriptStep, locale: string): string {
  if (isViLocale(locale) && step.title_vi && step.title_vi.trim()) return step.title_vi;
  return step.title;
}

export function resolveTourStepDescription(step: TourScriptStep, locale: string): string {
  if (isViLocale(locale) && step.description_vi && step.description_vi.trim())
    return step.description_vi;
  return step.description;
}

export function resolveTourScriptTitle(script: TourScript, locale: string): string {
  if (isViLocale(locale) && script.title_vi && script.title_vi.trim()) return script.title_vi;
  return script.title;
}

export type TourMode = 'autoplay' | 'interactive';
export type TourSpeed = 0.5 | 1 | 2;
export type TourEngineStatus = 'idle' | 'running' | 'paused' | 'finished' | 'failed';

interface TourEngineState {
  status: TourEngineStatus;
  currentStepIndex: number;
  mode: TourMode;
  speed: TourSpeed;
  script: TourScript | null;
}

interface TourEngineActions {
  start: (script: TourScript, mode?: TourMode, startStepIndex?: number) => void;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  nextStep: () => void;
  prevStep: () => void;
  setSpeed: (speed: TourSpeed) => void;
  setMode: (mode: TourMode) => void;
}

const SPEED_MULTIPLIERS: Record<TourSpeed, number> = {
  0.5: 2,
  1: 1,
  2: 0.5,
};

const DEFAULT_STEP_DURATION = 3000;
const DEFAULT_STEP_DELAY = 0;
const NAVIGATED_STEP_READINESS_TIMEOUT_MS = 15000;

export function useTourEngine(): [TourEngineState, TourEngineActions] {
  const navigate = useNavigate();
  const { locale, t } = useLocale();
  const blockedPageText = [t?.common?.no_permission, t?.common?.access_denied].filter(Boolean);
  const [state, setState] = useState<TourEngineState>({
    status: 'idle',
    currentStepIndex: 0,
    mode: (localStorage.getItem('grc_tour_mode') as TourMode) || 'autoplay',
    speed: 1,
    script: null,
  });
  const stateRef = useRef(state);
  stateRef.current = state;

  const driverRef = useRef<any>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef(false);
  const popoverCleanupRef = useRef<(() => void) | null>(null);
  const interactiveClickCleanupRef = useRef<(() => void) | null>(null);
  // Generation counter: incremented on every manual navigation to prevent stale
  // autoplay timers from firing and jumping back to a previous step.
  const generationRef = useRef(0);
  const readyStepRef = useRef(-1);
  // Sprint 63.44.0 (D7; gate E12.5 pass 1, P3-4) — the app's own popup ARIA on
  // every element the tour highlighted, restored whenever driver.js writes or
  // removes it (see `createDriverAriaGuard`).
  const ariaGuardRef = useRef<DriverAriaGuard | null>(null);
  ariaGuardRef.current ??= createDriverAriaGuard();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  function cleanup() {
    readyStepRef.current = -1;
    generationRef.current++;
    abortRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (interactiveClickCleanupRef.current) {
      interactiveClickCleanupRef.current();
      interactiveClickCleanupRef.current = null;
    }
    popoverCleanupRef.current?.();
    popoverCleanupRef.current = null;
    if (driverRef.current) {
      const tourDriver = driverRef.current;
      // driver.js removes its popup ARIA from the highlighted elements outright;
      // the guard gives each back whatever the app had set.
      ariaGuardRef.current?.destroy(() => {
        try {
          tourDriver.destroy();
        } catch (_) {
          // Preserve the existing optional fallback; report this failed operation without payloads.
          logWarning('hooks/useTourEngine::cleanup::1');
        }
      });
      driverRef.current = null;
    }
  }

  /**
   * Sprint 63.44.0 (D7) — highlight through driver.js without leaving its popup
   * ARIA where it does not belong. driver.js resolves the target with
   * `document.querySelector` and writes and removes the attributes
   * synchronously in `highlight`; the guard (`createDriverAriaGuard`) follows
   * the app's own values and puts them back after (a control being
   * highlighted keeps driver's).
   */
  function highlightWithoutPopupAria(selector: string, highlight: () => void) {
    // `null` when nothing matches (driver highlights its dummy element): that is
    // a highlight, not the end of the tour, so no record is dropped.
    const guard = ariaGuardRef.current;
    if (guard) guard.highlight(document.querySelector(selector), highlight);
    else highlight();
  }

  /** Bind to the actual driver render, including replacements created after animation. */
  function keepPopoverAboveController(popover: HTMLElement) {
    popoverCleanupRef.current?.();
    let disposed = false;
    let frame: number | null = null;
    let observedController: HTMLElement | null = null;
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(adjust);
    function adjust() {
      if (disposed || abortRef.current || !popover.isConnected) return;
      const controller = document.querySelector<HTMLElement>('.grc-tour-controller');
      if (!controller) return;
      if (observedController !== controller) {
        if (observedController) resizeObserver?.unobserve(observedController);
        resizeObserver?.observe(controller);
        observedController = controller;
      }
      const edge = 8;
      // Integer CSS pixels round-trip through CSSOM unchanged, including at zoom
      // levels where controller geometry has fractional coordinates.
      const ceiling = Math.floor(
        Math.min(window.innerHeight - edge, controller.getBoundingClientRect().top - 12),
      );
      // Long narration must scroll in the space ABOVE the toolbar. Moving a tall
      // popover to top:8 alone still leaves its bottom over the Next button.
      const maxHeight = `${Math.max(0, ceiling - edge)}px`;
      if (popover.style.maxHeight !== maxHeight) popover.style.maxHeight = maxHeight;
      const rect = popover.getBoundingClientRect();
      // A clipped narration region needs a keyboard stop for native scrolling.
      if (popover.scrollHeight > popover.clientHeight) popover.tabIndex = 0;
      else popover.removeAttribute('tabindex');
      const top = Math.max(edge, Math.floor(Math.min(rect.top, ceiling - rect.height)));
      if (rect.bottom > ceiling || rect.top < edge) {
        // Driver may carry a directional translate from its original placement.
        if (popover.style.transform !== 'none') popover.style.transform = 'none';
        if (popover.style.top !== `${top}px`) popover.style.top = `${top}px`;
        if (popover.style.bottom !== 'auto') popover.style.bottom = 'auto';
      }
    }
    // The driver invokes onPopoverRender before its final positioning writes.
    // Observe this exact wrapper synchronously, so those writes (and later
    // refreshes) are corrected before paint, not a frame after pointer overlap.
    const styleObserver = new MutationObserver(adjust);
    styleObserver.observe(popover, { attributes: true, attributeFilter: ['style'] });
    resizeObserver?.observe(popover);
    window.addEventListener('resize', adjust);
    window.addEventListener('scroll', adjust, true);
    frame = requestAnimationFrame(() => {
      frame = null;
      adjust();
    });
    popoverCleanupRef.current = () => {
      disposed = true;
      styleObserver.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', adjust);
      window.removeEventListener('scroll', adjust, true);
      if (frame !== null) cancelAnimationFrame(frame);
    };
    adjust();
  }

  async function waitForCommittedRoute(
    path: string,
    previousKey: string | null,
    gen: number,
    timeout = 5000,
  ) {
    const pathname = new URL(path, window.location.href).pathname;
    const started = Date.now();
    while (!abortRef.current && gen === generationRef.current && Date.now() - started < timeout) {
      const markers = document.querySelectorAll('[data-tour-route]');
      if (markers.length === 1) {
        const commitKey = markers[0].getAttribute('data-tour-route-key');
        if (
          markers[0].getAttribute('data-tour-route') === pathname &&
          commitKey &&
          commitKey !== previousKey
        )
          return true;
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    return false;
  }

  function waitForElement(selector: string, timeout = 5000, gen = generationRef.current) {
    return waitForTourElement(
      selector,
      timeout,
      () => abortRef.current || gen !== generationRef.current,
      blockedPageText,
    );
  }

  function executeAction(action: TourAction, timeout = 5000, gen = generationRef.current) {
    return executeTourAction(
      action,
      navigate,
      () => abortRef.current || gen !== generationRef.current,
      timeout,
    );
  }

  async function showStep(script: TourScript, index: number, mode: TourMode, speed: TourSpeed) {
    const gen = generationRef.current;
    if (abortRef.current) return;
    if (index >= script.steps.length) {
      cleanup();
      setState((s) => ({ ...s, status: 'finished' }));
      return;
    }

    const step = script.steps[index];
    readyStepRef.current = -1;
    setState((s) => ({ ...s, currentStepIndex: index }));
    const delay = step.delay_ms ?? DEFAULT_STEP_DELAY;
    const cancelled = () => abortRef.current || gen !== generationRef.current;
    const fail = () => {
      if (cancelled()) return;
      cleanup();
      setState((s) => ({ ...s, currentStepIndex: index, status: 'failed' }));
    };
    try {
      // A retained Suspense page can still match a generic selector after the
      // URL changes. Wait for the destination's committed page before anchors.
      if (step.navigate_to) {
        const previousKey =
          document.querySelector('[data-tour-route]')?.getAttribute('data-tour-route-key') ?? null;
        navigate(step.navigate_to);
        const committed = await waitForCommittedRoute(step.navigate_to, previousKey, gen);
        if (cancelled()) return;
        if (!committed) {
          fail();
          return;
        }
      }

      // A committed page may still be waiting for its initial API reads. Give
      // its readiness selector and target one bounded budget, not one each.
      const readinessDeadline = step.navigate_to
        ? Date.now() + NAVIGATED_STEP_READINESS_TIMEOUT_MS
        : null;

      // Wait for explicit wait_for selector
      if (step.wait_for) {
        const ready = await waitForElement(
          step.wait_for,
          readinessDeadline === null ? 5000 : Math.max(0, readinessDeadline - Date.now()),
          gen,
        );
        if (cancelled()) return;
        if (!ready) {
          fail();
          return;
        }
      }

      // The target uses whatever remains of the destination's readiness budget.
      const waitTimeout =
        readinessDeadline !== null
          ? Math.max(0, readinessDeadline - Date.now())
          : mode === 'interactive'
            ? 300
            : 1500;
      const targetEl = await waitForElement(step.target, waitTimeout, gen);

      if (cancelled()) return;
      if (!targetEl) {
        fail();
        return;
      }

      // Optional settle delay (0 by default, only used if step explicitly sets delay_ms)
      if (delay > 0) {
        await new Promise((r) => setTimeout(r, delay));
        if (cancelled()) return;
      }

      if (step.scroll_to !== false && targetEl) {
        targetEl.scrollIntoView({ behavior: 'instant', block: 'center' });
      }

      // Use driver.js to highlight
      if (driverRef.current) {
        try {
          const tourDriver = driverRef.current;
          highlightWithoutPopupAria(step.target, () =>
            tourDriver.highlight({
              element: step.target,
              popover: {
                title: resolveTourStepTitle(step, locale),
                description: resolveTourStepDescription(step, locale),
                side: step.position || 'bottom',
                align: 'center',
              },
            }),
          );
        } catch (_) {
          fail();
          return;
        }
      }

      setState((s) => ({ ...s, currentStepIndex: index }));

      readyStepRef.current = index;
      // Clean up any previous interactive click listener before attaching a new one
      if (interactiveClickCleanupRef.current) {
        interactiveClickCleanupRef.current();
        interactiveClickCleanupRef.current = null;
      }

      // In auto-play mode, execute action and auto-advance
      if (mode === 'autoplay') {
        if (step.action) {
          // Pause so the user can see the highlight before the action fires.
          // Use a longer delay when the step also navigated (page needs to settle).
          if (step.action.type === 'click') {
            const preActionDelay = step.navigate_to ? 3000 : 2500;
            await new Promise((r) => setTimeout(r, preActionDelay));
          }
          if (abortRef.current || generationRef.current !== gen) return;
          await executeAction(step.action, 1500, gen);
          if (generationRef.current !== gen) return;
          // Brief pause after action so result is visible
          if (step.action.type === 'fill') {
            await new Promise((r) => setTimeout(r, 200));
          }
        }

        if (abortRef.current) return;

        // If the user manually navigated while this step was executing, abort
        if (generationRef.current !== gen) return;

        const duration = (step.duration_ms ?? DEFAULT_STEP_DURATION) * SPEED_MULTIPLIERS[speed];
        timerRef.current = setTimeout(() => {
          if (generationRef.current !== gen) return;
          showStep(script, index + 1, mode, speed);
        }, duration);
      } else {
        // Interactive mode: clicking the target element advances to the next step.
        // For non-click actions, the action is also executed before advancing so
        // both the target click and the Next button produce the same result.
        if (targetEl instanceof HTMLElement) {
          const handleTargetClick = () => {
            if (cancelled()) return;
            interactiveClickCleanupRef.current = null;
            if (step.action && step.action.type !== 'click') {
              const action = step.action;
              executeAction(action, 1500, gen)
                .then(() => {
                  if (!cancelled()) showStep(script, index + 1, mode, speed);
                })
                .catch(fail);
            } else {
              showStep(script, index + 1, mode, speed);
            }
          };
          targetEl.addEventListener('click', handleTargetClick, { once: true });
          interactiveClickCleanupRef.current = () =>
            targetEl.removeEventListener('click', handleTargetClick);
        }
      }
    } catch (_) {
      fail();
    }
  }

  const start = useCallback(
    (script: TourScript, mode?: TourMode, startStepIndex = 0) => {
      cleanup();
      abortRef.current = false;
      generationRef.current++;

      const tourMode = mode || state.mode;
      localStorage.setItem('grc_tour_mode', tourMode);

      const stepIdx = Math.min(Math.max(0, startStepIndex), script.steps.length - 1);

      driverRef.current = createDriver({
        showProgress: false,
        animate: !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
        smoothScroll: false,
        allowClose: false,
        allowKeyboardControl: false,
        stagePadding: 8,
        stageRadius: 8,
        popoverClass: 'grc-tour-popover',
        onPopoverRender: ({ wrapper }) => keepPopoverAboveController(wrapper),
        showButtons: [],
        overlayOpacity: 0.6,
        onDestroyed: () => {
          // Tour ended externally
        },
      });

      setState({
        status: 'running',
        currentStepIndex: stepIdx,
        mode: tourMode,
        speed: state.speed,
        script,
      });

      showStep(script, stepIdx, tourMode, state.speed);
    },
    [state.mode, state.speed],
  );

  const stop = useCallback(() => {
    cleanup();
    abortRef.current = false;
    setState({
      status: 'idle',
      currentStepIndex: 0,
      mode: state.mode,
      speed: state.speed,
      script: null,
    });
  }, [state.mode, state.speed]);

  const pause = useCallback(() => {
    generationRef.current++;
    if (timerRef.current) clearTimeout(timerRef.current);
    interactiveClickCleanupRef.current?.();
    interactiveClickCleanupRef.current = null;
    setState((s) => ({ ...s, status: 'paused' }));
  }, []);

  const resume = useCallback(() => {
    const gen = ++generationRef.current;
    setState((s) => {
      if (s.status === 'paused' && s.script) {
        setTimeout(() => {
          if (gen === generationRef.current)
            showStep(s.script!, s.currentStepIndex, s.mode, s.speed);
        }, 0);
        return { ...s, status: 'running' };
      }
      return s;
    });
  }, []);

  const nextStep = useCallback(() => {
    const s = stateRef.current;
    if (!s.script || s.status === 'failed' || s.status === 'finished') return;
    if (readyStepRef.current !== s.currentStepIndex) return;
    // Consume readiness synchronously, before React renders, so a second Next
    // cannot execute the same step twice. Lifecycle effects belong here, never
    // in a state updater: StrictMode may replay that updater after cleanup has
    // invalidated readiness, which used to leave the final step running forever.
    readyStepRef.current = -1;
    if (timerRef.current) clearTimeout(timerRef.current);
    const gen = ++generationRef.current;
    interactiveClickCleanupRef.current?.();
    interactiveClickCleanupRef.current = null;
    const next = s.currentStepIndex + 1;
    const currentStep = s.script.steps[s.currentStepIndex];
    // A hurried Next cannot turn an absent screen into successful instruction.
    if (
      !findVisibleTourTarget(currentStep.target, blockedPageText) ||
      (currentStep.wait_for && !findVisibleTourTarget(currentStep.wait_for, blockedPageText))
    ) {
      cleanup();
      const failed = { ...s, status: 'failed' as const };
      stateRef.current = failed;
      setState(failed);
      return;
    }
    if (next >= s.script.steps.length) {
      cleanup();
      abortRef.current = false;
      const finished = { ...s, status: 'finished' as const };
      stateRef.current = finished;
      setState(finished);
      return;
    }
    const running = { ...s, currentStepIndex: next, status: 'running' as const };
    stateRef.current = running;
    setState(running);
    setTimeout(async () => {
      if (gen !== generationRef.current) return;
      try {
        // Execute the current step's action before advancing (mirrors autoplay).
        if (s.mode === 'interactive' && currentStep.action) {
          await executeAction(currentStep.action, 1500, gen);
          if (currentStep.action.type === 'fill' || currentStep.action.type === 'select') {
            await new Promise((r) => setTimeout(r, 200));
          }
        }
        if (gen !== generationRef.current) return;
        showStep(s.script!, next, s.mode, s.speed);
      } catch (_) {
        if (gen !== generationRef.current) return;
        cleanup();
        setState((current) => ({ ...current, status: 'failed' }));
      }
    }, 0);
  }, []);

  const prevStep = useCallback(() => {
    setState((s) => {
      if (!s.script || s.status === 'failed' || s.status === 'finished' || s.currentStepIndex <= 0)
        return s;
      if (timerRef.current) clearTimeout(timerRef.current);
      generationRef.current++;
      const gen = generationRef.current;
      const prev = s.currentStepIndex - 1;
      setTimeout(() => {
        if (gen === generationRef.current) showStep(s.script!, prev, s.mode, s.speed);
      }, 0);
      return { ...s, currentStepIndex: prev, status: 'running' };
    });
  }, []);

  const setSpeed = useCallback((speed: TourSpeed) => {
    restartPlayback({ speed });
  }, []);

  const setMode = useCallback((mode: TourMode) => {
    localStorage.setItem('grc_tour_mode', mode);
    restartPlayback({ mode });
  }, []);

  function restartPlayback(change: Partial<Pick<TourEngineState, 'speed' | 'mode'>>) {
    generationRef.current++;
    const gen = generationRef.current;
    if (timerRef.current) clearTimeout(timerRef.current);
    interactiveClickCleanupRef.current?.();
    interactiveClickCleanupRef.current = null;
    setState((s) => {
      const next = { ...s, ...change };
      if (next.status === 'running' && next.script) {
        setTimeout(() => {
          if (gen === generationRef.current)
            showStep(next.script!, next.currentStepIndex, next.mode, next.speed);
        }, 0);
      }
      return next;
    });
  }

  return [state, { start, stop, pause, resume, nextStep, prevStep, setSpeed, setMode }];
}
